import {
  GENERATION_EQUIPMENT_CATALOG_VERSION,
  GENERATION_EQUIPMENT_DUTY_HASH_VERSION,
  type GenerationAssumption,
  type GenerationEquipmentBasis,
  type GenerationEquipmentDataStatus,
  type GenerationEquipmentExhibitBasis,
  type GenerationEquipmentPublishedAcoustics,
  type GenerationImpactResult,
  type GenerationImpactScenarioInput,
  type GenerationNoiseUnit,
  type GenerationEquipmentRatingConfirmation,
  type GenerationEquipmentRatingScope,
  type GenerationTechnology,
} from '../../types/generationImpact';
import { canonicalJson, hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { solveGenerationImpact } from './solveGenerationImpact';

export const BTU_PER_KILOJOULE = 0.947_817_120_313_317_2;

/**
 * GENERATION-ISLAND land intensity, acres per site-rated GROSS MW.
 *
 * Convention (settled 2026-07-25): `footprint.plantAcresPerGrossMW` covers the
 * GENERATION ISLAND ONLY. Cooling and switchyard are separate additive figures
 * (`coolingAcres` / `switchyardAcres`) and the studio draws them as their own
 * rectangles, so anything folded in here would be counted twice.
 *
 * These are PLANNING SURROGATES, not OEM data. Neither published basis models
 * an island: the OEM `generationBlockEnvelope` is the gas-turbine PACKAGE only
 * (its own `limitation` field says "not the full 1×1 combined-cycle plant"),
 * and the previous 0.4 acre/MW was a WHOLE-SITE figure — it put a ~460 MW block
 * on 184 acres, roughly 10-15x an actual island.
 *
 * Island scope = prime mover(s) + heat-recovery/exhaust + steam turbine hall
 * (CC only) + step-up transformers + maintenance and fire access. It EXCLUDES
 * cooling, switchyard, laydown, roads and the CCS train.
 *
 * ⚠️ Pending OEM general-arrangement drawings. Replace per entry as vendor
 * island GAs arrive, and re-check the fit before trusting a tight parcel.
 */
export const GENERATION_ISLAND_ACRES_PER_GROSS_MW = {
  /**
   * Large F/H-class 1×1 combined cycle. Denser per MW than reciprocating
   * plant because one large train carries the whole block output.
   */
  'combined-cycle-block': 0.025,
  /**
   * Medium-speed reciprocating engine hall. Less dense per MW: many small
   * units, each with its own radiator bay, exhaust stack and service aisle.
   */
  'reciprocating-engine': 0.05,
} as const;

/**
 * EPA screening conversion for natural gas: LHV / HHV = 0.91. The actual
 * project fuel composition and contractual heating-value basis must replace
 * this approximation in the vendor heat balance.
 */
export const NATURAL_GAS_LHV_TO_HHV_RATIO = 0.91;

const NATURAL_GAS_HEATING_VALUE_SOURCE = {
  publisher: 'U.S. Environmental Protection Agency',
  title: 'GHG Reporting Program supplemental calculation methodology — natural gas LHV/HHV ratio',
  url: 'https://nepis.epa.gov/Exe/ZyPURL.cgi?Dockey=P10108MT.TXT',
  retrievedAt: '2026-07-18',
} as const;

/**
 * Screening-reference acoustic default shared by the shipped entries: one
 * per-block source at 85 dBA measured 3.048 m (10 ft) from the equipment
 * boundary. This is the industry near-field design basis (GE GER-4239,
 * "Power Plant Near-Field Noise Considerations" — the 85 dBA near-field
 * average design practice, gevernova.com) combined with this platform's
 * existing 85 dBA @ 10 ft planning convention. It is deliberately NOT an
 * OEM guarantee for any specific model — per-model guaranteed sound-power
 * levels are contractual and not publicly published — so `basis` stays
 * 'screening-reference' and `dataStatus.acousticSourceData` stays
 * 'oem-data-required' until a per-model vendor citation upgrades it.
 */
const SCREENING_REFERENCE_ACOUSTICS: GenerationEquipmentPublishedAcoustics = {
  basis: 'screening-reference',
  datum: {
    kind: 'reference-spl',
    levelDbA: 85,
    referenceDistanceM: 3.048,
    scope: 'per-unit',
    basisLabel: 'Screening reference: 85 dBA at 10 ft per generation block (industry near-field design basis)',
  },
  operatingCase: 'Base load, enclosure-adjacent near field',
  sourceNote: 'Screening default per the industry near-field noise design basis (GE GER-4239, "Power Plant Near-Field Noise Considerations", gevernova.com; 85 dBA near-field average design practice) and this platform\'s 85 dBA @ 10 ft planning convention. Not an OEM guarantee for this model — replace with vendor octave-band/guaranteed data when available.',
};

type ScreeningNormalization = GenerationEquipmentBasis['screeningNormalization'];

export interface GenerationEquipmentCatalogEntry extends Omit<
  GenerationEquipmentBasis,
  'appliedDutyFingerprint' | 'screeningNormalization'
> {
  screeningNormalization: Omit<ScreeningNormalization, 'appliedBlockCount'>;
}

const OEM_DATA_REQUIRED: GenerationEquipmentDataStatus = 'oem-data-required';

function lhvKjToHhvBtu(heatRateKjPerKWh: number): number {
  return heatRateKjPerKWh * BTU_PER_KILOJOULE / NATURAL_GAS_LHV_TO_HHV_RATIO;
}

function normalizedNetPlantDuty(
  netOutputMW: number,
  netHeatRateKjPerKWhLhv: number,
  auxiliaryFractionOfIsoGross: number,
): { solverIsoGrossMWPerBlock: number; solverHeatRateBtuPerKWhHhv: number } {
  const netFraction = 1 - auxiliaryFractionOfIsoGross;
  const solverIsoGrossMWPerBlock = netOutputMW / netFraction;
  // The canonical solver applies heat rate to gross output. Scale the OEM net
  // heat rate by net/gross so ISO fuel input remains identical after the
  // explicit auxiliary load is subtracted.
  const solverHeatRateBtuPerKWhHhv = lhvKjToHhvBtu(netHeatRateKjPerKWhLhv)
    * netOutputMW / solverIsoGrossMWPerBlock;
  return { solverIsoGrossMWPerBlock, solverHeatRateBtuPerKWhHhv };
}

const GE_7HA01_DUTY = normalizedNetPlantDuty(438, 5_783, 0.04);
const SIEMENS_SCC6_5000F_DUTY = normalizedNetPlantDuty(391, 5_950, 0.04);

const catalogEntries: GenerationEquipmentCatalogEntry[] = [
  {
    catalogVersion: GENERATION_EQUIPMENT_CATALOG_VERSION,
    dutyHashVersion: GENERATION_EQUIPMENT_DUTY_HASH_VERSION,
    publishedAcoustics: SCREENING_REFERENCE_ACOUSTICS,
    catalogId: 'ge-7ha01-1x1-cc-60hz',
    manufacturer: 'GE Vernova',
    model: '7HA.01',
    trainConfiguration: '1×1 combined-cycle block · 60 Hz',
    technology: 'combined-cycle-block',
    sourceKind: 'oem-rating',
    confirmationRequired: false,
    gasEngineOrTurbineCountPerBlock: 1,
    contingencyElement: 'combined-cycle-block',
    commonModeBoundary: 'The N-1 screen removes one complete 1×1 block, including its gas turbine, HRSG, steam cycle, generator path, and block auxiliaries.',
    publishedRating: {
      outputMWPerBlock: 438,
      ratingScope: 'net-plant',
      referenceConditions: 'OEM net plant rating at ISO conditions on natural gas; actual performance varies with project conditions and fuel.',
      heatRateValue: 5_783,
      heatRateUnit: 'kJ/kWh',
      heatingValueBasis: 'LHV',
      efficiencyPct: 62.3,
      projectPrecedentMW: null,
    },
    publishedPhysicalReference: null,
    screeningNormalization: {
      ...GE_7HA01_DUTY,
      ambientFactor: 0.9,
      degradationFactor: 0.98,
      auxiliaryFractionOfIsoGross: 0.04,
      forcedOutageRate: null,
      generationWithdrawalGalPerMWhGross: 500,
      generationConsumptionGalPerMWhGross: 400,
      plantAcresPerGrossMW: GENERATION_ISLAND_ACRES_PER_GROSS_MW['combined-cycle-block'],
      footprintPowerBasis: 'site-rated-gross',
      coolingAcres: 10,
      switchyardAcres: 10,
      coolingSystem: 'closed-loop-wet',
      naturalGasLhvToHhvRatio: NATURAL_GAS_LHV_TO_HHV_RATIO,
      normalizationNote: '438 MW OEM net output is algebraically normalized to solver gross output using a 4% planning auxiliary fraction. The OEM LHV net heat rate is converted to an HHV gross-output basis using the EPA screening ratio LHV/HHV = 0.91 so ISO fuel input is conserved. The land surrogate is a GENERATION-ISLAND intensity (prime mover, heat recovery, steam turbine hall, step-up transformers, maintenance and fire access) applied to site-rated gross output; cooling and switchyard are counted separately. It is a planning figure, not an OEM installed footprint — the published OEM envelope covers the gas-turbine package only — so it varies with the planning derate and must be replaced by a vendor general-arrangement drawing before it is relied on.',
    },
    dataStatus: {
      ambientCorrectionCurve: OEM_DATA_REQUIRED,
      coolingHeatBalance: 'planning-assumption',
      fuelGasPressure: OEM_DATA_REQUIRED,
      acousticSourceData: OEM_DATA_REQUIRED,
      installedPlantFootprint: 'planning-assumption',
      ccsIntegration: OEM_DATA_REQUIRED,
      reliabilityAndMaintenance: OEM_DATA_REQUIRED,
    },
    unavailableFields: [
      'Site ambient correction curve',
      'Required fuel-gas inlet pressure and composition envelope',
      'Installed 1×1 plant dimensions and maintenance clearances',
      'Octave-band sound-power data and guaranteed boundary noise',
      'Absolute water balance for the selected cooling system',
      'CCS derate, tie-ins, capture footprint, export corridor, and construction laydown',
      'Contractual availability, maintenance schedule, and common-cause outage basis',
    ],
    publishedSources: [
      {
        publisher: 'GE Vernova',
        title: 'GE Vernova Gas Power Catalog — 7HA power plants',
        url: 'https://www.gevernova.com/content/dam/gepower-new/global/en_US/downloads/noindexpdf/GEA35241-GE-Vernova-Gas-Power-Catalog.pdf',
        retrievedAt: '2026-07-18',
        revision: 'GEA35241',
      },
      NATURAL_GAS_HEATING_VALUE_SOURCE,
    ],
  },
  {
    catalogVersion: GENERATION_EQUIPMENT_CATALOG_VERSION,
    dutyHashVersion: GENERATION_EQUIPMENT_DUTY_HASH_VERSION,
    publishedAcoustics: SCREENING_REFERENCE_ACOUSTICS,
    catalogId: 'siemens-scc6-5000f-1x1-60hz',
    manufacturer: 'Siemens Energy',
    model: 'SCC6-5000F',
    trainConfiguration: '1×1 combined-cycle block · 60 Hz',
    technology: 'combined-cycle-block',
    sourceKind: 'oem-rating',
    confirmationRequired: true,
    gasEngineOrTurbineCountPerBlock: 1,
    contingencyElement: 'combined-cycle-block',
    commonModeBoundary: 'The N-1 screen would remove one complete 1×1 block. OEM confirmation is required because current Siemens sources do not reconcile the published plant rating.',
    publishedRating: {
      outputMWPerBlock: 391,
      ratingScope: 'net-plant',
      referenceConditions: 'Current OEM PDF net rating used for comparison only; the current product page also publishes unreconciled 398 MW gross and 387 MW overview values.',
      heatRateValue: 5_950,
      heatRateUnit: 'kJ/kWh',
      heatingValueBasis: 'LHV',
      efficiencyPct: 60.5,
      projectPrecedentMW: null,
    },
    publishedPhysicalReference: {
      scope: 'gas-turbine-package',
      lengthM: 50,
      widthM: 29,
      heightM: 9,
      massTonnes: null,
      sourceTitle: 'SGT6-5000F gas turbine product page',
      limitation: 'Gas-turbine package reference only, including the gas turbine, generator, intake, exhaust, auxiliaries, controls, and electrical systems; not the full 1×1 combined-cycle plant or its maintenance envelope.',
    },
    screeningNormalization: {
      ...SIEMENS_SCC6_5000F_DUTY,
      ambientFactor: 0.9,
      degradationFactor: 0.98,
      auxiliaryFractionOfIsoGross: 0.04,
      forcedOutageRate: null,
      generationWithdrawalGalPerMWhGross: 500,
      generationConsumptionGalPerMWhGross: 400,
      plantAcresPerGrossMW: GENERATION_ISLAND_ACRES_PER_GROSS_MW['combined-cycle-block'],
      footprintPowerBasis: 'site-rated-gross',
      coolingAcres: 10,
      switchyardAcres: 10,
      coolingSystem: 'closed-loop-wet',
      naturalGasLhvToHhvRatio: NATURAL_GAS_LHV_TO_HHV_RATIO,
      normalizationNote: 'The 391 MW PDF net rating is normalized for comparison using a 4% planning auxiliary fraction and the EPA screening ratio LHV/HHV = 0.91. Direct application is disabled until Siemens reconciles the current 391 MW net, 398 MW gross, and 387 MW overview values. The land surrogate is a GENERATION-ISLAND intensity applied to site-rated gross output, with cooling and switchyard counted separately; it is a planning figure, not an OEM installed footprint (the published OEM envelope covers the gas-turbine package only).',
    },
    dataStatus: {
      ambientCorrectionCurve: OEM_DATA_REQUIRED,
      coolingHeatBalance: 'planning-assumption',
      fuelGasPressure: OEM_DATA_REQUIRED,
      acousticSourceData: OEM_DATA_REQUIRED,
      installedPlantFootprint: OEM_DATA_REQUIRED,
      ccsIntegration: OEM_DATA_REQUIRED,
      reliabilityAndMaintenance: OEM_DATA_REQUIRED,
    },
    unavailableFields: [
      'Reconciled contractual gross and net plant rating',
      'Site ambient correction curve',
      'Required fuel-gas inlet pressure and composition envelope',
      'Full 1×1 plant footprint beyond the published gas-turbine package envelope',
      'Cooling-system water balance and sound-power spectrum',
      'CCS derate, tie-ins, capture footprint, export corridor, and construction laydown',
      'Contractual availability, maintenance schedule, and common-cause outage basis',
    ],
    publishedSources: [
      {
        publisher: 'Siemens Energy',
        title: 'SGT6-5000F / SCC6-5000F technical data',
        url: 'https://assets.siemens-energy.com/dam/752d00e3-2a9c-492b-97d7-b3b60095f76e/CB3-25-0041-GT-EN-01-%283%29-pdf_Original%20file.pdf',
        retrievedAt: '2026-07-18',
        revision: 'CB3-25-0041-GT-EN-01',
      },
      {
        publisher: 'Siemens Energy',
        title: 'SGT6-5000F gas turbine product page',
        url: 'https://www.siemens-energy.com/global/en/home/products-services/product/sgt6-5000f.html',
        retrievedAt: '2026-07-18',
      },
      NATURAL_GAS_HEATING_VALUE_SOURCE,
    ],
  },
  {
    catalogVersion: GENERATION_EQUIPMENT_CATALOG_VERSION,
    dutyHashVersion: GENERATION_EQUIPMENT_DUTY_HASH_VERSION,
    publishedAcoustics: SCREENING_REFERENCE_ACOUSTICS,
    catalogId: 'wartsila-24x18v50sg-electrical-island-60hz',
    manufacturer: 'Wärtsilä',
    model: '18V50SG',
    trainConfiguration: '24-engine electrical island · 60 Hz',
    technology: 'reciprocating-engine',
    sourceKind: 'derived-aggregation',
    confirmationRequired: false,
    gasEngineOrTurbineCountPerBlock: 24,
    contingencyElement: 'electrical-island',
    commonModeBoundary: 'The N-1 screen removes one entire 24-engine electrical island. Individual-engine outage resilience and shared gas, cooling, controls, switchgear, and collection common modes require a separate reliability model.',
    publishedRating: {
      outputMWPerBlock: 24 * 18.875,
      ratingScope: 'generator-terminals',
      referenceConditions: 'Arithmetic aggregation of 24 OEM 60 Hz generating-set ratings at 100 kPa, 25°C suction air, 30% relative humidity, power factor 1.0, excluding engine-driven pumps. The leaflet states a 5% efficiency tolerance and that its data are non-contractual; this is not an OEM plant guarantee.',
      heatRateValue: 7_207,
      heatRateUnit: 'kJ/kWh',
      heatingValueBasis: 'LHV',
      efficiencyPct: 50,
      projectPrecedentMW: 429,
    },
    publishedPhysicalReference: {
      scope: 'generating-set-transport-envelope',
      lengthM: 18.747,
      widthM: 5.543,
      heightM: 6.257,
      massTonnes: 377,
      sourceTitle: 'Wärtsilä 50SG product leaflet',
      limitation: 'Maximum transport dimensions and dry weight for one generating set; not an installed building, radiator, balance-of-plant, or maintenance-clearance envelope.',
    },
    screeningNormalization: {
      solverIsoGrossMWPerBlock: 24 * 18.875,
      ambientFactor: 0.95,
      degradationFactor: 0.98,
      auxiliaryFractionOfIsoGross: 0.03,
      solverHeatRateBtuPerKWhHhv: lhvKjToHhvBtu(7_207),
      forcedOutageRate: null,
      generationWithdrawalGalPerMWhGross: 20,
      generationConsumptionGalPerMWhGross: 15,
      plantAcresPerGrossMW: GENERATION_ISLAND_ACRES_PER_GROSS_MW['reciprocating-engine'],
      footprintPowerBasis: 'site-rated-gross',
      coolingAcres: 10,
      switchyardAcres: 10,
      coolingSystem: 'closed-loop-radiator',
      naturalGasLhvToHhvRatio: NATURAL_GAS_LHV_TO_HHV_RATIO,
      normalizationNote: 'The solver uses the 453 MW arithmetic generator-terminal sum and converts the LHV heat rate with the EPA screening ratio LHV/HHV = 0.91. The separately published 429 MW data-center project precedent is not treated as an auxiliary, ambient, or emissions derate because the OEM source does not explain the difference. The applied 20/15 gal/MWh withdrawal/consumption values are conservative planning surrogates, not the OEM family claim; the OEM data-center page separately states process-water consumption below 5 L/hour, pending a project-specific contractual water balance. The land surrogate is a GENERATION-ISLAND intensity applied to site-rated gross output, with cooling and switchyard counted separately; it is a planning figure, not an OEM installed footprint (the published OEM envelope covers the gas-turbine package only).',
    },
    dataStatus: {
      ambientCorrectionCurve: 'published-family-only',
      coolingHeatBalance: 'planning-assumption',
      fuelGasPressure: OEM_DATA_REQUIRED,
      acousticSourceData: OEM_DATA_REQUIRED,
      installedPlantFootprint: 'planning-assumption',
      ccsIntegration: OEM_DATA_REQUIRED,
      reliabilityAndMaintenance: OEM_DATA_REQUIRED,
    },
    unavailableFields: [
      'Contractual plant net rating for a 24-engine electrical island',
      'Combined temperature-altitude correction curve for the selected 50SG configuration',
      'Required fuel-gas inlet pressure and composition envelope',
      'Installed building, maintenance, radiator, and balance-of-plant footprint',
      'Project-specific contractual water balance reconciling the family process-water claim',
      'Octave-band sound-power data and guaranteed boundary noise',
      'CCS exhaust collection, derate, corridor, and construction laydown',
      'Contractual engine/island availability, maintenance schedule, and common-cause outage basis',
    ],
    publishedSources: [
      {
        publisher: 'Wärtsilä',
        title: 'Wärtsilä 50SG product leaflet',
        url: 'https://www.wartsila.com/docs/default-source/energy-docs/technology-products/product-leaflets/wartsila-50sg.pdf',
        retrievedAt: '2026-07-18',
      },
      {
        publisher: 'Wärtsilä',
        title: '24-engine, 429 MW U.S. data-center project announcement',
        url: 'https://www.wartsila.com/usa/media/news/29-01-2026-wartsila-chosen-for-a-major-u-s-power-plant-project-addressing-critical-energy-demand-driven-by-data-center-development-3711601',
        retrievedAt: '2026-07-18',
        publishedAt: '2026-01-29',
      },
      {
        publisher: 'Wärtsilä',
        title: 'Data-center power solutions',
        url: 'https://www.wartsila.com/energy/engine-power-plant-solutions/flexible-baseload-power-plants/data-center-power-solutions',
        retrievedAt: '2026-07-18',
      },
      NATURAL_GAS_HEATING_VALUE_SOURCE,
    ],
  },
];

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value as Record<string, unknown>).forEach(deepFreeze);
  }
  return value;
}

export const GENERATION_EQUIPMENT_CATALOG: readonly GenerationEquipmentCatalogEntry[] =
  deepFreeze(catalogEntries);

/** Stable id for the noise source synthesized from a catalog acoustic figure. */
export const EQUIPMENT_ACOUSTIC_SOURCE_ID = 'equipment-acoustics';

/**
 * Synthesize the scenario noise sources implied by a basis/entry acoustic
 * figure: one source whose count equals the applied block count, carrying the
 * per-block datum. Shared by the apply path and the expected-duty fingerprint
 * so the two can never drift.
 */
export function equipmentAcousticNoiseSources(
  acoustics: GenerationEquipmentPublishedAcoustics,
  manufacturer: string,
  model: string,
  blockCount: number,
): GenerationNoiseUnit[] {
  return [{
    id: EQUIPMENT_ACOUSTIC_SOURCE_ID,
    name: `${manufacturer} ${model} acoustic source (${acoustics.basis === 'oem-guarantee' ? 'OEM guarantee' : 'screening reference'})`,
    count: blockCount,
    noise: structuredClone(acoustics.datum),
  }];
}

function physicalBlockCount(input: GenerationImpactScenarioInput): number {
  return input.units.reduce(
    (sum, unit) => sum + (Number.isInteger(unit.count) && unit.count > 0 ? unit.count : 0),
    0,
  );
}

function dutyFingerprintPayload(input: GenerationImpactScenarioInput): unknown {
  return {
    units: input.units.map(unit => ({
      technology: unit.technology,
      contingencyGroupId: unit.contingencyGroupId,
      count: unit.count,
      isoGrossMWPerUnit: unit.isoGrossMWPerUnit,
      ambientFactor: unit.ambientFactor,
      degradationFactor: unit.degradationFactor,
      auxiliaryLoadMWPerUnit: unit.auxiliaryLoadMWPerUnit,
      heatRateBtuPerKWhHhv: unit.heatRateBtuPerKWhHhv,
      forcedOutageRate: unit.forcedOutageRate ?? null,
      noise: unit.noise ?? null,
      soundPowerDbA: unit.soundPowerDbA ?? null,
      directivityCorrectionDb: unit.directivityCorrectionDb ?? null,
      barrierLossDb: unit.barrierLossDb ?? null,
      noxLbPerMWh: unit.noxLbPerMWh ?? null,
      coLbPerMWh: unit.coLbPerMWh ?? null,
    })),
    noiseSources: input.noiseSources ?? null,
    water: {
      generationWithdrawalGalPerMWhGross:
        input.water.generationWithdrawalGalPerMWhGross,
      generationConsumptionGalPerMWhGross:
        input.water.generationConsumptionGalPerMWhGross,
    },
    footprint: input.footprint,
  };
}

function expectedAppliedDutyFingerprint(
  entry: GenerationEquipmentCatalogEntry,
  blockCount: number,
  /**
   * Whether the applied duty includes catalog-synthesized acoustic sources.
   * MUST mirror the presence of `publishedAcoustics` on the SAVED basis
   * being applied/validated — never the live catalog — so bases saved
   * before the acoustics generation keep reproducing their fingerprints.
   */
  includeAcoustics: boolean,
): string {
  const screening = entry.screeningNormalization;
  return hashCanonicalJson({
    units: Array.from({ length: blockCount }, (_, index) => ({
      technology: entry.technology,
      contingencyGroupId: `equipment-group-${index + 1}`,
      count: 1,
      isoGrossMWPerUnit: screening.solverIsoGrossMWPerBlock,
      ambientFactor: screening.ambientFactor,
      degradationFactor: screening.degradationFactor,
      auxiliaryLoadMWPerUnit:
        screening.solverIsoGrossMWPerBlock * screening.auxiliaryFractionOfIsoGross,
      heatRateBtuPerKWhHhv: screening.solverHeatRateBtuPerKWhHhv,
      forcedOutageRate: screening.forcedOutageRate,
      noise: null,
      soundPowerDbA: null,
      directivityCorrectionDb: null,
      barrierLossDb: null,
      noxLbPerMWh: null,
      coLbPerMWh: null,
    })),
    noiseSources: includeAcoustics && entry.publishedAcoustics
      ? equipmentAcousticNoiseSources(
          entry.publishedAcoustics,
          entry.manufacturer,
          entry.model,
          blockCount,
        )
      : null,
    water: {
      generationWithdrawalGalPerMWhGross:
        screening.generationWithdrawalGalPerMWhGross,
      generationConsumptionGalPerMWhGross:
        screening.generationConsumptionGalPerMWhGross,
    },
    footprint: {
      plantAcresPerGrossMW: screening.plantAcresPerGrossMW,
      coolingAcres: screening.coolingAcres,
      switchyardAcres: screening.switchyardAcres,
    },
  });
}

/** Hash only the equipment-linked duty, excluding site, CCS, and report metadata. */
export function hashGenerationEquipmentDuty(input: GenerationImpactScenarioInput): string {
  return hashCanonicalJson(dutyFingerprintPayload(input));
}

function replaceEquipmentAssumptions(
  assumptions: readonly GenerationAssumption[],
  entry: GenerationEquipmentCatalogEntry,
): GenerationAssumption[] {
  const retained = assumptions.filter(assumption => (
    assumption.field !== 'technologyArchetype'
    && !assumption.field.startsWith('equipment')
    && !assumption.field.startsWith('projectDuty')
  ));
  const citation = entry.publishedSources.map(source => source.url).join(' | ');
  return [
    ...structuredClone(retained),
    {
      field: 'technologyArchetype',
      value: entry.technology,
      source: 'vendor',
      label: `${entry.manufacturer} ${entry.model} equipment identity`,
      citation,
      confidence: entry.confirmationRequired ? 'medium' : 'high',
    },
    {
      field: 'equipmentPublishedRating',
      value: entry.publishedRating.outputMWPerBlock,
      source: entry.sourceKind === 'derived-aggregation' ? 'planning-default' : 'vendor',
      label: entry.sourceKind === 'derived-aggregation'
        ? `${entry.publishedRating.ratingScope} arithmetic equipment aggregation in MW per block`
        : `${entry.publishedRating.ratingScope} published output in MW per block`,
      citation,
      confidence: entry.confirmationRequired ? 'low' : 'high',
    },
    {
      field: 'equipmentScreeningNormalization',
      value: entry.screeningNormalization.solverIsoGrossMWPerBlock,
      source: 'planning-default',
      label: 'Solver ISO-gross normalization in MW per block',
      citation: entry.screeningNormalization.normalizationNote,
      confidence: 'low',
    },
    {
      field: 'equipmentDataGaps',
      value: entry.unavailableFields.join('; '),
      source: 'planning-default',
      label: 'OEM / EPC data request remaining after catalog selection',
      confidence: 'low',
    },
  ];
}

export interface ApplyGenerationEquipmentEnvelopeOptions {
  /**
   * Internal comparison-only escape hatch for entries with conflicting OEM
   * sources. NOT a user-facing override — comparison boards and alternatives
   * ranking need to price every entry, including unreconciled ones. User paths
   * must supply a `confirmation` instead, so the resolution carries evidence.
   */
  allowUnconfirmed?: boolean;
  /**
   * An engineer's attested reconciliation of the entry's conflicting published
   * ratings. A VALID confirmation unblocks the entry for real use and rides
   * with the scenario as provenance.
   */
  confirmation?: GenerationEquipmentRatingConfirmation;
}

/**
 * Validate an attested rating reconciliation. Returns [] when usable.
 *
 * The bar is deliberately evidence-shaped, not consent-shaped: a confirmation
 * without a source reference or an attesting person is indistinguishable from
 * clicking "use anyway", which is exactly what this mechanism exists to avoid.
 */
export function validateEquipmentRatingConfirmation(
  confirmation: GenerationEquipmentRatingConfirmation,
  catalogId: string,
): readonly string[] {
  const errors: string[] = [];
  if (confirmation.catalogId !== catalogId) {
    errors.push(`confirmation is for ${confirmation.catalogId}, not ${catalogId}`);
  }
  if (!Number.isFinite(confirmation.confirmedOutputMW)
    || confirmation.confirmedOutputMW <= 0
    || confirmation.confirmedOutputMW > 5_000) {
    errors.push('confirmed output must be between 0 and 5,000 MW');
  }
  const SCOPES: readonly GenerationEquipmentRatingScope[] = ['net-plant', 'gross-plant', 'generator-terminals'];
  if (!SCOPES.includes(confirmation.confirmedRatingScope)) {
    errors.push('confirmed rating scope must be net-plant, gross-plant, or generator-terminals');
  }
  if (typeof confirmation.sourceReference !== 'string' || confirmation.sourceReference.trim().length < 3) {
    errors.push('a source reference is required (vendor email, quote number, or signed datasheet)');
  }
  if (typeof confirmation.confirmedBy !== 'string' || confirmation.confirmedBy.trim().length < 2) {
    errors.push('the confirming engineer must be recorded');
  }
  if (!confirmation.confirmedAt || Number.isNaN(Date.parse(confirmation.confirmedAt))) {
    errors.push('a valid confirmation timestamp is required');
  }
  return errors;
}

/**
 * Does this scenario carry a valid attestation unblocking `catalogId`?
 * Pure — the caller decides what to do about it.
 */
export function hasValidRatingConfirmation(
  input: Pick<GenerationImpactScenarioInput, 'equipmentRatingConfirmations'>,
  catalogId: string,
): boolean {
  const confirmation = input.equipmentRatingConfirmations?.[catalogId];
  if (!confirmation) return false;
  return validateEquipmentRatingConfirmation(confirmation, catalogId).length === 0;
}

/**
 * Record an attestation onto the scenario, unblocking the entry for real use.
 * Pure; throws on an invalid attestation rather than storing a useless one.
 */
export function recordEquipmentRatingConfirmation(
  source: GenerationImpactScenarioInput,
  confirmation: GenerationEquipmentRatingConfirmation,
): GenerationImpactScenarioInput {
  const errors = validateEquipmentRatingConfirmation(confirmation, confirmation.catalogId);
  if (errors.length > 0) {
    throw new RangeError(`Cannot record this OEM confirmation — ${errors.join('; ')}.`);
  }
  const next = structuredClone(source);
  next.equipmentRatingConfirmations = {
    ...(next.equipmentRatingConfirmations ?? {}),
    [confirmation.catalogId]: { ...confirmation },
  };
  return next;
}

export interface AddGenerationEquipmentUnitsOptions extends ApplyGenerationEquipmentEnvelopeOptions {
  /** Physical blocks of this equipment to add. Defaults to 1. */
  blockCount?: number;
}

/**
 * MIXED FLEET — add a catalog entry to the scenario as ADDITIONAL units instead
 * of replacing the fleet (`applyGenerationEquipmentEnvelope` rewrites the whole
 * duty for a single-technology fleet).
 *
 * Physics: the solver already sizes and sums `input.units` heterogeneously, and
 * water/footprint aggregate per unit whenever EVERY unit declares its own
 * intensities. So this function:
 *
 *  1. seeds the new units' intensities from the catalog entry, and
 *  2. BACKFILLS the incumbent units with the scenario's current plant-wide
 *     intensities — the very figures that already described them.
 *
 * Step 2 is what makes the change conservative: each incumbent unit keeps
 * contributing exactly what it contributed before (`Σ grossMW × plant-wide
 * intensity` is the same number whether the intensity is read once for the
 * fleet or once per unit), and the appended technology adds only its own
 * correct share. `coolingAcres` / `switchyardAcres` are site-level fixed
 * acreages, not per-MW intensities, so they are deliberately left untouched.
 *
 * The caller owns re-solving; this is a pure input transform.
 */
export function addGenerationEquipmentUnits(
  source: GenerationImpactScenarioInput,
  catalogId: string,
  options: AddGenerationEquipmentUnitsOptions = {},
): GenerationImpactScenarioInput {
  const entry = GENERATION_EQUIPMENT_CATALOG.find(candidate => candidate.catalogId === catalogId);
  if (!entry) throw new RangeError(`Unknown generation equipment catalog id: ${catalogId}`);
  // A confirmationRequired entry is unblocked by EITHER an attested
  // reconciliation (the user path — carries evidence) or the internal
  // comparison escape hatch. Both are explicit; neither is a silent default.
  const confirmed = options.confirmation != null
    && validateEquipmentRatingConfirmation(options.confirmation, catalogId).length === 0;
  if (entry.confirmationRequired && options.allowUnconfirmed !== true && !confirmed) {
    throw new RangeError(`${entry.manufacturer} ${entry.model} publishes conflicting ratings, so it cannot be added until an engineer records the reconciled figure and its source. ${entry.publishedRating.referenceConditions}`);
  }
  const blockCount = options.blockCount ?? 1;
  if (!Number.isInteger(blockCount) || blockCount < 1 || blockCount > 1_000) {
    throw new RangeError('Adding equipment requires between 1 and 1,000 physical generation blocks.');
  }
  const screening = entry.screeningNormalization;
  const next = structuredClone(source);

  // Incumbent units inherit the plant-wide intensities that already described
  // them, so their water/footprint contribution is numerically unchanged.
  next.units = next.units.map(unit => ({
    ...unit,
    generationWithdrawalGalPerMWhGross:
      unit.generationWithdrawalGalPerMWhGross ?? source.water.generationWithdrawalGalPerMWhGross,
    generationConsumptionGalPerMWhGross:
      unit.generationConsumptionGalPerMWhGross ?? source.water.generationConsumptionGalPerMWhGross,
    plantAcresPerGrossMW:
      unit.plantAcresPerGrossMW ?? source.footprint.plantAcresPerGrossMW,
  }));

  // Unique ids/groups so the added blocks never collide with the incumbents
  // (a shared contingencyGroupId would corrupt the N-1 largest-contingency test).
  const usedIds = new Set(next.units.map(unit => unit.id));
  const usedGroups = new Set(next.units.map(unit => unit.contingencyGroupId));
  const uniqueSuffix = (base: string, used: Set<string>): string => {
    let candidate = base;
    let n = 2;
    while (used.has(candidate)) candidate = `${base}-${n++}`;
    used.add(candidate);
    return candidate;
  };

  for (let index = 0; index < blockCount; index += 1) {
    const id = uniqueSuffix(`${entry.catalogId}-block-${index + 1}`, usedIds);
    next.units.push({
      id,
      label: `${entry.model} block ${index + 1}`,
      technology: entry.technology,
      contingencyGroupId: uniqueSuffix(`${entry.catalogId}-group-${index + 1}`, usedGroups),
      count: 1,
      isoGrossMWPerUnit: screening.solverIsoGrossMWPerBlock,
      ambientFactor: screening.ambientFactor,
      degradationFactor: screening.degradationFactor,
      auxiliaryLoadMWPerUnit:
        screening.solverIsoGrossMWPerBlock * screening.auxiliaryFractionOfIsoGross,
      heatRateBtuPerKWhHhv: screening.solverHeatRateBtuPerKWhHhv,
      ...(screening.forcedOutageRate == null ? {} : { forcedOutageRate: screening.forcedOutageRate }),
      // This technology's OWN intensities — the point of the mixed fleet.
      generationWithdrawalGalPerMWhGross: screening.generationWithdrawalGalPerMWhGross,
      generationConsumptionGalPerMWhGross: screening.generationConsumptionGalPerMWhGross,
      plantAcresPerGrossMW: screening.plantAcresPerGrossMW,
    });
  }

  // Append this equipment's acoustic source alongside the incumbents' rather
  // than replacing the scenario's sources. Entries without a published figure
  // add nothing, leaving the existing acoustic basis exactly as it was.
  if (entry.publishedAcoustics) {
    const added = equipmentAcousticNoiseSources(
      entry.publishedAcoustics,
      entry.manufacturer,
      entry.model,
      blockCount,
    ).map(unitSource => ({
      ...unitSource,
      id: `${EQUIPMENT_ACOUSTIC_SOURCE_ID}:${entry.catalogId}`,
    }));
    next.noiseSources = [...(next.noiseSources ?? []), ...added];
  }

  return next;
}

/**
 * Apply one immutable equipment envelope without mutating site evidence, CCS,
 * parcel geometry, receptors, lighting, or documented resource capacities.
 */
export function applyGenerationEquipmentEnvelope(
  source: GenerationImpactScenarioInput,
  catalogId: string,
  options: ApplyGenerationEquipmentEnvelopeOptions = {},
): GenerationImpactScenarioInput {
  const entry = GENERATION_EQUIPMENT_CATALOG.find(candidate => candidate.catalogId === catalogId);
  if (!entry) throw new RangeError(`Unknown generation equipment catalog id: ${catalogId}`);
  // A confirmationRequired entry is unblocked by EITHER an attested
  // reconciliation (the user path — carries evidence) or the internal
  // comparison escape hatch. Both are explicit; neither is a silent default.
  const confirmed = options.confirmation != null
    && validateEquipmentRatingConfirmation(options.confirmation, catalogId).length === 0;
  if (entry.confirmationRequired && options.allowUnconfirmed !== true && !confirmed) {
    throw new RangeError(`${entry.manufacturer} ${entry.model} publishes conflicting ratings, so it cannot be applied until an engineer records the reconciled figure and its source. ${entry.publishedRating.referenceConditions}`);
  }
  const blockCount = physicalBlockCount(source);
  if (!Number.isInteger(blockCount) || blockCount < 1 || blockCount > 1_000) {
    throw new RangeError('Equipment selection requires between 1 and 1,000 physical generation blocks.');
  }
  const screening = entry.screeningNormalization;
  const applied = structuredClone(source);
  const restoration = source.projectDutyBasis?.restorationBaseline;
  if (restoration
    && Number.isFinite(restoration.ccsCaptureFootprintAcres)
    && restoration.ccsCaptureFootprintAcres >= 0
    && Number.isFinite(restoration.ccsCompressionFootprintAcres)
    && restoration.ccsCompressionFootprintAcres >= 0) {
    applied.ccs.footprintAcres = restoration.ccsCaptureFootprintAcres;
    applied.ccs.compressionFootprintAcres = restoration.ccsCompressionFootprintAcres;
  }
  // Mirrors the CCS restore above. Without this a dossier-derived firm gas
  // capacity would survive a dossier clear or an equipment change and silently
  // constrain an unrelated fleet. `null` recorded "was undocumented", so the
  // restore DELETES rather than zeroes; the key is absent on every dossier
  // written before the interconnect duty existed, so legacy scenarios skip this.
  if (restoration && 'availableGasCapacityDthPerDay' in restoration) {
    const baseline = restoration.availableGasCapacityDthPerDay;
    if (baseline == null) delete applied.availableGasCapacityDthPerDay;
    else if (Number.isFinite(baseline) && baseline >= 0) {
      applied.availableGasCapacityDthPerDay = baseline;
    }
  }
  applied.units = Array.from({ length: blockCount }, (_, index) => ({
    id: `equipment-block-${index + 1}`,
    label: `${entry.model} block ${index + 1}`,
    technology: entry.technology,
    contingencyGroupId: `equipment-group-${index + 1}`,
    count: 1,
    isoGrossMWPerUnit: screening.solverIsoGrossMWPerBlock,
    ambientFactor: screening.ambientFactor,
    degradationFactor: screening.degradationFactor,
    auxiliaryLoadMWPerUnit:
      screening.solverIsoGrossMWPerBlock * screening.auxiliaryFractionOfIsoGross,
    heatRateBtuPerKWhHhv: screening.solverHeatRateBtuPerKWhHhv,
    ...(screening.forcedOutageRate == null
      ? {}
      : { forcedOutageRate: screening.forcedOutageRate }),
  }));
  // Catalog acoustic figure (screening default or OEM guarantee) synthesizes
  // the scenario noise sources; entries without one keep the historical
  // delete so their applied duty — and fingerprint — stay byte-identical.
  if (entry.publishedAcoustics) {
    applied.noiseSources = equipmentAcousticNoiseSources(
      entry.publishedAcoustics,
      entry.manufacturer,
      entry.model,
      blockCount,
    );
  } else {
    delete applied.noiseSources;
  }
  applied.water = {
    ...applied.water,
    generationWithdrawalGalPerMWhGross: screening.generationWithdrawalGalPerMWhGross,
    generationConsumptionGalPerMWhGross: screening.generationConsumptionGalPerMWhGross,
  };
  delete applied.water.generationDesignDay;
  applied.footprint = {
    plantAcresPerGrossMW: screening.plantAcresPerGrossMW,
    coolingAcres: screening.coolingAcres,
    switchyardAcres: screening.switchyardAcres,
  };
  applied.assumptions = replaceEquipmentAssumptions(applied.assumptions, entry);
  // A catalog envelope supersedes any applied user entry (workspace metadata).
  if (applied.equipmentWorkspace?.appliedUserEquipmentId != null) {
    delete applied.equipmentWorkspace.appliedUserEquipmentId;
  }
  const selection: GenerationEquipmentBasis = {
    ...structuredClone(entry),
    screeningNormalization: {
      appliedBlockCount: blockCount,
      ...structuredClone(screening),
    },
    appliedDutyFingerprint: '',
  };
  applied.equipmentBasis = selection;
  delete applied.projectDutyBasis;
  selection.appliedDutyFingerprint = expectedAppliedDutyFingerprint(entry, blockCount, true);
  return applied;
}

function nearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= Math.max(1, Math.abs(left), Math.abs(right)) * 1e-9;
}

export function evaluateGenerationEquipmentAlignment(
  input: GenerationImpactScenarioInput,
): Omit<GenerationEquipmentExhibitBasis, 'selection'> | null {
  const selection = input.equipmentBasis;
  if (!selection) return null;
  const currentDutyFingerprint = hashGenerationEquipmentDuty(input);
  if (currentDutyFingerprint === selection.appliedDutyFingerprint) {
    return { currentDutyFingerprint, alignmentStatus: 'aligned', alignmentNotes: [] };
  }

  const notes: string[] = [];
  const screening = selection.screeningNormalization;
  if (physicalBlockCount(input) !== screening.appliedBlockCount) {
    notes.push('Physical block count differs from the catalog-applied duty.');
  }
  const unitsAligned = input.units.every((unit, index) => (
    unit.count === 1
    && unit.technology === selection.technology
    && unit.contingencyGroupId === `equipment-group-${index + 1}`
    && nearlyEqual(unit.isoGrossMWPerUnit, screening.solverIsoGrossMWPerBlock)
    && nearlyEqual(unit.ambientFactor, screening.ambientFactor)
    && nearlyEqual(unit.degradationFactor, screening.degradationFactor)
    && nearlyEqual(
      unit.auxiliaryLoadMWPerUnit,
      screening.solverIsoGrossMWPerBlock * screening.auxiliaryFractionOfIsoGross,
    )
    && nearlyEqual(unit.heatRateBtuPerKWhHhv, screening.solverHeatRateBtuPerKWhHhv)
    && (unit.forcedOutageRate ?? null) === screening.forcedOutageRate
    && unit.noise == null
    && unit.soundPowerDbA == null
    && unit.directivityCorrectionDb == null
    && unit.barrierLossDb == null
    && unit.noxLbPerMWh == null
    && unit.coLbPerMWh == null
  ));
  if (!unitsAligned || input.noiseSources != null) {
    notes.push('Power-block rating, contingency grouping, derate, auxiliary, heat-rate, outage, emissions, or acoustic inputs differ from the catalog-applied duty.');
  }
  if (
    !nearlyEqual(
      input.water.generationWithdrawalGalPerMWhGross,
      screening.generationWithdrawalGalPerMWhGross,
    )
    || !nearlyEqual(
      input.water.generationConsumptionGalPerMWhGross,
      screening.generationConsumptionGalPerMWhGross,
    )
  ) {
    notes.push('Generation water intensities differ from the catalog-applied planning basis.');
  }
  if (
    !nearlyEqual(input.footprint.plantAcresPerGrossMW, screening.plantAcresPerGrossMW)
    || !nearlyEqual(input.footprint.coolingAcres, screening.coolingAcres)
    || !nearlyEqual(input.footprint.switchyardAcres, screening.switchyardAcres)
  ) {
    notes.push('Plant, cooling, or switchyard land allowances differ from the catalog-applied planning basis.');
  }
  if (notes.length === 0) notes.push('Equipment-linked duty fields changed after catalog application.');
  return { currentDutyFingerprint, alignmentStatus: 'modified', alignmentNotes: notes };
}

export function projectGenerationEquipmentExhibitBasis(
  input: GenerationImpactScenarioInput,
): GenerationEquipmentExhibitBasis | undefined {
  const selection = input.equipmentBasis;
  const alignment = evaluateGenerationEquipmentAlignment(input);
  return selection && alignment
    ? { selection: structuredClone(selection), ...alignment }
    : undefined;
}

export interface GenerationEquipmentScreenRow {
  catalogId: string;
  manufacturer: string;
  model: string;
  trainConfiguration: string;
  sourceKind: GenerationEquipmentBasis['sourceKind'];
  confirmationRequired: boolean;
  gasEngineOrTurbineCountPerBlock: number;
  publishedOutputMWPerBlock: number;
  publishedRatingScope: GenerationEquipmentBasis['publishedRating']['ratingScope'];
  publishedHeatRateValue: number | null;
  publishedHeatRateUnit: GenerationEquipmentBasis['publishedRating']['heatRateUnit'];
  publishedHeatingValueBasis: GenerationEquipmentBasis['publishedRating']['heatingValueBasis'];
  publishedEfficiencyPct: number | null;
  publishedPhysicalReference: GenerationEquipmentBasis['publishedPhysicalReference'];
  projectPrecedentMW: number | null;
  solverIsoGrossMWPerBlock: number;
  solverHeatRateBtuPerKWhHhv: number;
  naturalGasLhvToHhvRatio: number | null;
  siteGrossMWPerBlock: number;
  siteNetMWPerBlock: number;
  result: GenerationImpactResult;
  dataStatus: GenerationEquipmentBasis['dataStatus'];
  unavailableFields: readonly string[];
  publishedSources: readonly GenerationEquipmentBasis['publishedSources'][number][];
  normalizationNote: string;
  commonModeBoundary: string;
}

/** Compare every catalog envelope through the unchanged canonical solver. */
export function buildGenerationEquipmentScreen(
  input: GenerationImpactScenarioInput,
  // ADMISSIONS SEAM — the host may pass the Firestore-admitted subset
  // (generationEquipmentCatalogAdmissions). Entries are always the shipped
  // static objects, so solver duty values stay version-locked regardless of
  // which subset is offered. Default = the full shipped catalog (unchanged).
  catalog: readonly GenerationEquipmentCatalogEntry[] = GENERATION_EQUIPMENT_CATALOG,
): readonly GenerationEquipmentScreenRow[] {
  return catalog.map(entry => {
    const candidate = applyGenerationEquipmentEnvelope(input, entry.catalogId, {
      allowUnconfirmed: true,
    });
    const result = solveGenerationImpact(candidate);
    return {
      catalogId: entry.catalogId,
      manufacturer: entry.manufacturer,
      model: entry.model,
      trainConfiguration: entry.trainConfiguration,
      sourceKind: entry.sourceKind,
      confirmationRequired: entry.confirmationRequired,
      gasEngineOrTurbineCountPerBlock: entry.gasEngineOrTurbineCountPerBlock,
      publishedOutputMWPerBlock: entry.publishedRating.outputMWPerBlock,
      publishedRatingScope: entry.publishedRating.ratingScope,
      publishedHeatRateValue: entry.publishedRating.heatRateValue,
      publishedHeatRateUnit: entry.publishedRating.heatRateUnit,
      publishedHeatingValueBasis: entry.publishedRating.heatingValueBasis,
      publishedEfficiencyPct: entry.publishedRating.efficiencyPct,
      publishedPhysicalReference: structuredClone(entry.publishedPhysicalReference),
      projectPrecedentMW: entry.publishedRating.projectPrecedentMW,
      solverIsoGrossMWPerBlock: entry.screeningNormalization.solverIsoGrossMWPerBlock,
      solverHeatRateBtuPerKWhHhv:
        entry.screeningNormalization.solverHeatRateBtuPerKWhHhv,
      naturalGasLhvToHhvRatio:
        entry.screeningNormalization.naturalGasLhvToHhvRatio,
      siteGrossMWPerBlock: result.unitResults[0]?.siteGrossMWPerUnit ?? 0,
      siteNetMWPerBlock: result.unitResults[0]?.netMWPerUnit ?? 0,
      result,
      dataStatus: structuredClone(entry.dataStatus),
      unavailableFields: [...entry.unavailableFields],
      publishedSources: structuredClone(entry.publishedSources),
      normalizationNote: entry.screeningNormalization.normalizationNote,
      commonModeBoundary: entry.commonModeBoundary,
    };
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function finiteInRange(value: unknown, minimum: number, maximum: number): boolean {
  return typeof value === 'number'
    && Number.isFinite(value)
    && value >= minimum
    && value <= maximum;
}

/** Runtime guard used by frozen exhibit validation. */
export function validateGenerationEquipmentBasis(value: unknown): readonly string[] {
  if (!isRecord(value)) return ['equipment basis must be an object'];
  const errors: string[] = [];
  const requiredStrings = [
    'catalogId',
    'manufacturer',
    'model',
    'trainConfiguration',
    'commonModeBoundary',
  ] as const;
  requiredStrings.forEach(field => {
    if (typeof value[field] !== 'string' || value[field].length === 0 || value[field].length > 2_000) {
      errors.push(`${field} must be a non-empty bounded string`);
    }
  });
  if (value.catalogVersion !== GENERATION_EQUIPMENT_CATALOG_VERSION) {
    errors.push('catalogVersion is unsupported');
  }
  if (value.dutyHashVersion !== GENERATION_EQUIPMENT_DUTY_HASH_VERSION) {
    errors.push('dutyHashVersion is unsupported');
  }
  if (typeof value.appliedDutyFingerprint !== 'string'
    || !/^[a-f\d]{64}$/i.test(value.appliedDutyFingerprint)) {
    errors.push('appliedDutyFingerprint must be a SHA-256 digest');
  }
  if (!finiteInRange(value.gasEngineOrTurbineCountPerBlock, 1, 10_000)
    || !Number.isInteger(value.gasEngineOrTurbineCountPerBlock)) {
    errors.push('gasEngineOrTurbineCountPerBlock must be a positive bounded integer');
  }
  const rating = value.publishedRating;
  if (!isRecord(rating) || !finiteInRange(rating.outputMWPerBlock, 0.001, 100_000)) {
    errors.push('publishedRating.outputMWPerBlock must be finite and positive');
  }
  const screening = value.screeningNormalization;
  if (!isRecord(screening)) {
    errors.push('screeningNormalization must be an object');
  } else {
    if (!finiteInRange(screening.appliedBlockCount, 1, 1_000)
      || !Number.isInteger(screening.appliedBlockCount)) {
      errors.push('screeningNormalization.appliedBlockCount must be an integer in [1, 1000]');
    }
    const positive = ['solverIsoGrossMWPerBlock', 'solverHeatRateBtuPerKWhHhv'] as const;
    positive.forEach(field => {
      if (!finiteInRange(screening[field], 0.000_001, 100_000)) {
        errors.push(`screeningNormalization.${field} must be finite and positive`);
      }
    });
    ['ambientFactor', 'degradationFactor'].forEach(field => {
      if (!finiteInRange(screening[field], 0.000_001, 1)) {
        errors.push(`screeningNormalization.${field} must be in (0, 1]`);
      }
    });
    if (!finiteInRange(screening.auxiliaryFractionOfIsoGross, 0, 0.99)) {
      errors.push('screeningNormalization.auxiliaryFractionOfIsoGross must be in [0, 0.99]');
    }
    if (screening.forcedOutageRate != null
      && !finiteInRange(screening.forcedOutageRate, 0, 1)) {
      errors.push('screeningNormalization.forcedOutageRate must be null or in [0, 1]');
    }
  }
  if (!Array.isArray(value.unavailableFields)
    || value.unavailableFields.some(item => typeof item !== 'string' || item.length > 2_000)) {
    errors.push('unavailableFields must be a bounded string array');
  }
  if (!Array.isArray(value.publishedSources) || value.publishedSources.length < 1 || value.publishedSources.length > 10) {
    errors.push('publishedSources must contain between 1 and 10 source objects');
  } else {
    value.publishedSources.forEach((source, index) => {
      if (!isRecord(source)) {
        errors.push(`publishedSources[${index}] must be an object`);
        return;
      }
      if (typeof source.publisher !== 'string' || typeof source.title !== 'string') {
        errors.push(`publishedSources[${index}] requires publisher and title`);
      }
      if (typeof source.url !== 'string' || !source.url.startsWith('https://')) {
        errors.push(`publishedSources[${index}].url must use HTTPS`);
      }
      if (typeof source.retrievedAt !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(source.retrievedAt)) {
        errors.push(`publishedSources[${index}].retrievedAt must be YYYY-MM-DD`);
      }
    });
  }

  const entry = typeof value.catalogId === 'string'
    ? GENERATION_EQUIPMENT_CATALOG.find(candidate => candidate.catalogId === value.catalogId)
    : undefined;
  if (!entry) {
    errors.push('catalogId is not present in the immutable equipment catalog');
  } else {
    if (entry.confirmationRequired) {
      errors.push('catalog entry requires OEM confirmation and cannot be persisted as an applied basis');
    }
    const appliedBlockCount = isRecord(screening)
      ? screening.appliedBlockCount
      : undefined;
    if (isRecord(screening)
      && typeof appliedBlockCount === 'number'
      && Number.isInteger(appliedBlockCount)
      && finiteInRange(appliedBlockCount, 1, 1_000)
      && typeof value.appliedDutyFingerprint === 'string') {
      // Acoustics generation-awareness: bases saved BEFORE a catalog entry
      // gained `publishedAcoustics` carry neither the field nor the
      // synthesized noise sources in their fingerprint. Both checks key off
      // the SAVED basis's own field presence — never the live catalog — so
      // older applied bases remain valid and 'aligned' after a seed lands.
      const savedHasAcoustics = 'publishedAcoustics' in value
        && value.publishedAcoustics !== undefined;
      const authority = structuredClone(entry) as GenerationEquipmentCatalogEntry;
      if (!savedHasAcoustics) delete authority.publishedAcoustics;
      const expectedBasis = {
        ...authority,
        screeningNormalization: {
          appliedBlockCount,
          ...structuredClone(entry.screeningNormalization),
        },
        appliedDutyFingerprint: value.appliedDutyFingerprint,
      };
      try {
        if (canonicalJson(value) !== canonicalJson(expectedBasis)) {
          errors.push('equipment basis does not exactly match its immutable catalog authority');
        }
      } catch (error) {
        errors.push(`equipment basis cannot be compared with its catalog authority: ${(error as Error).message}`);
      }
      if (value.appliedDutyFingerprint !== expectedAppliedDutyFingerprint(
        entry,
        appliedBlockCount,
        savedHasAcoustics,
      )) {
        errors.push('appliedDutyFingerprint does not match the catalog-applied duty');
      }
    }
  }
  return errors;
}

export function technologyLabel(technology: GenerationTechnology): string {
  if (technology === 'combined-cycle-block') return 'Combined cycle';
  if (technology === 'simple-cycle-gas-turbine') return 'Simple cycle';
  return 'Reciprocating engines';
}
